import * as React from 'react';
import cn from 'classnames';
import { Props as PassengerProps } from '../../Passenger/components/Passenger';
import Header from './components/Header/Header';
import FormFields from './components/FormFields/FormFields';
import { useTheme } from '../../theme';
import { getPassengerDataLabel, requiredFillData } from '../utils';
import { useForm, useFormState } from 'react-final-form';
import PopupWrapper from './components/PopupWrapper/PopupWrapper';
import { useMediaQuery } from 'react-responsive';
import {
	concatPassengerNames,
	convertGenderToCorrectValue,
	getPassengerTypeLabel,
	MOBILE_MIN_WIDTH
} from '../../utils';
import { fieldIsRequired, fieldsArrayToMap } from '../../Passenger/utils';
import WindowScope from './components/WindowScope/WindowScope';
import { Collapse } from '@mui/material';
import {
	DocTypes,
	Gender,
	InfoMessagePositionsEnum,
	InfoMessagesFragment,
	TravellerFieldEnum,
	TravellerProfileFragment
} from '@websky/graphql';
import { AdditionalTravellerFieldEnum, PassengerField } from '../../Passenger/types';
import { useRenderers } from '../../renderProps';
import { TravellerFields } from '../../TravellerForm/types';
import InfoMessages from '../../InfoMessages';
import { filterInfoMessagesByCode } from '../../Checkout/utils';
import { CdnIcon } from '@shared/ui';

export type Props = PassengerProps & {
	className?: string;
	selectedProfileId?: string;
	open?: boolean;
	autofocus?: boolean;
	isChangeFiles?: boolean;
	onCorrectFilled?: (passengerId: number) => void;
	accompanyingFields?: PassengerField[];
	onSignUpLoyaltyProgram?: () => void;
	onSwap?: () => void;
	onToggleChangeFiles?: (expanded: boolean) => void;
	infoMessages: InfoMessagesFragment[];
	hideClearButton?: boolean;
};

const PassengerForm: React.FC<Props> = props => {
	const theme = useTheme('PassengerForm').PassengerForm;

	const { change } = useForm();
	const { values, submitFailed } = useFormState();

	const label = React.useMemo(() => {
		const passengerValues: Record<string, string> = values.passengers[props.id];

		const firstName = passengerValues?.[TravellerFields.FirstName];
		const lastName = passengerValues?.[TravellerFields.LastName];
		const middleName = passengerValues?.[TravellerFields.MiddleName];

		if (firstName && lastName) {
			return <span className={theme.label}>{concatPassengerNames({ lastName, firstName, middleName })}</span>;
		}

		return getPassengerTypeLabel(props.passengerType, props.supplierType);
	}, [props.fields, values]);

	const docType = React.useMemo(() => values.passengers[props.id]?.docType, [values, props.id]);

	const isFieldRequired = React.useMemo(
		() =>
			requiredFillData(values?.passengers?.[props.id], values?.customer, props.fields, props.accompanyingFields),
		[values, props.fields]
	);

	const fieldsMap = React.useMemo(() => {
		return fieldsArrayToMap(props.fields);
	}, [props.fields]);

	const { PassengerFormFields: PassengerFormFieldsOverride = FormFields } = useRenderers();

	const passengerFieldsMap = React.useMemo(() => {
		if (!isFieldRequired) {
			return getPassengerDataLabel(values.passengers?.[props.id], fieldsMap);
		}

		return {};
	}, [isFieldRequired, values, fieldsMap]);

	React.useEffect(() => {
		if (!isFieldRequired) {
			props.onCorrectFilled(props.id);
		}
	}, [isFieldRequired]);

	const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });
	const [hasErrors, setHasErrors] = React.useState(false);

	const isOpened = isMobile && props.mobileOpenedPassengerId === props.id;
	const isReadonly = (props.isInputsReadonly || props.isReadonly) && !props.isChangePassengers;

	const numberIcon = React.useMemo(() => {
		const gender =
			props.isChangePassengers &&
			props.fields.find(field => field.type === TravellerFieldEnum.Gender)?.defaultValue;

		if (!gender) {
			return null;
		}

		return <CdnIcon icon={convertGenderToCorrectValue(gender) === Gender.Male ? 'Info/Male' : 'Info/Female'} />;
	}, [props.fields, props.isChangePassengers]);

	const fields = React.useMemo(() => {
		if (props.isChangePassengers) {
			const isPassport = docType === DocTypes.Passport;

			return props.fields.filter(field => {
				if (
					[
						TravellerFieldEnum.LinkedTraveller,
						TravellerFieldEnum.Snils,
						AdditionalTravellerFieldEnum.Subsidy
					].includes(field.type)
				) {
					return false;
				}

				return !isPassport || (isPassport && field.type !== TravellerFieldEnum.DocExpiryDate);
			});
		}

		return props.fields;
	}, [docType, props.fields, props.isChangePassengers]);

	React.useEffect(() => {
		if (isMobile && submitFailed && isFieldRequired && props.open) {
			setHasErrors(true);

			if (props.mobileOpenedPassengerId === null) {
				props.onSetMobileOpenedPassengerId(props.id);
			}
		} else if (!isFieldRequired) {
			setHasErrors(false);
		}
	}, [submitFailed, isMobile, isFieldRequired, props.open]);

	const onCloseHandler = () => props.onSetMobileOpenedPassengerId(null);
	const onOpenHandler = () => props.onSetMobileOpenedPassengerId(props.id);

	const onFillHandler = (profile: TravellerProfileFragment, type?: TravellerFieldEnum, fieldValue?: string) => {
		if (type) {
			const docExpiryValidations =
				props.fields.find(field => field.type === TravellerFieldEnum.DocExpiryDate)?.validations || [];

			if (
				(type === TravellerFieldEnum.Nationality || type === TravellerFieldEnum.DocType) &&
				(values.passengers?.[props.id]?.docExpiryDate || values.passengers?.[props.id]?.nationality) &&
				fieldIsRequired(values.passengers[props.id], docExpiryValidations)
			) {
				change(`passengers[${props.id}].docExpiryDate`, '');
			}
		}

		props.onFill?.(profile, fieldValue);
	};

	const filteredInfoMessages = React.useMemo(() => {
		const passenger = values.passengers?.[props.id];

		return passenger && props.infoMessages ? filterInfoMessagesByCode(props.infoMessages, [passenger]) : [];
	}, [props.infoMessages, values.passengers, props.id]);

	const showInfoMessages = filteredInfoMessages.length > 0;

	return (
		<div
			className={cn(theme.form, props.className)}
			data-test-id="passenger-form"
			data-passenger-type={props.passengerType}
			data-passenger-id={props.id}
		>
			<WindowScope fieldsMap={fieldsMap} disabled={isReadonly}>
				<Header
					open={props.open}
					passengerId={props.id}
					label={label}
					numberIcon={numberIcon}
					onClick={onOpenHandler}
					correct={!isFieldRequired}
					passengerFieldsMap={passengerFieldsMap}
					showErrorNotice={hasErrors}
					hideClearButton={props.hideClearButton}
				/>

				<Collapse in={props.open} unmountOnExit mountOnEnter>
					<PopupWrapper
						inline={!isMobile}
						open={isOpened}
						onClose={onCloseHandler}
						correct={!isFieldRequired}
					>
						{showInfoMessages && (
							<InfoMessages
								className={theme.infoMessages}
								messages={filteredInfoMessages}
								position={[InfoMessagePositionsEnum.passengerCard]}
							/>
						)}

						<PassengerFormFieldsOverride
							fields={fields}
							passengerId={props.id}
							passengerType={props.passengerType}
							passengerDocType={docType}
							selectedProfileId={props.selectedProfileId}
							label={label}
							inline={!isMobile}
							disabled={isReadonly}
							onTravellerSelect={onFillHandler}
							isAuthorized={props.isAuthorized}
							isChangeFiles={props.isChangeFiles}
							isChangePassengers={props.isChangePassengers}
							autofocus={props.autofocus}
							accompanyingFields={props.accompanyingFields}
							onSignUpLoyaltyProgram={props.onSignUpLoyaltyProgram}
							onSwap={props.onSwap}
							onToggleChangeFiles={props.onToggleChangeFiles}
						/>
					</PopupWrapper>
				</Collapse>
			</WindowScope>
		</div>
	);
};

export default PassengerForm;
